import clsx from "clsx";
import { useMemo, useState } from "react";
import { LoadingSkeleton } from "@/components/common";
import { CommonInputProps } from "@/interfaces/form";
import { getNestedProps } from "@/utils";
import { InputError } from "../InputError";

interface Props extends CommonInputProps {
  optionalLabel?: string;
  rows?: number;
  outlined?: boolean;
  underlined?: boolean;
  maxLength?: number;
  resizable?: boolean;
  required?: boolean;
}

export const InputTextArea = ({
  className,
  field,
  label,
  control,
  disabled,
  outlined = true,
  underlined = false,
  placeholder,
  rows = 4,
  maxLength,
  optionalLabel,
  resizable,
  required,
}: Props): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const registration = control.register(field);

  const errorMessage = useMemo(() => {
    return getNestedProps(control.errors, field)?.message;
  }, [control.errors, field]);

  const borderStyle = useMemo(() => {
    if (errorMessage) {
      return "border-error";
    }

    if (!outlined && !underlined) {
      return "border-transparent";
    }

    return "border-input-border";
  }, [errorMessage, outlined, underlined, isFocused]);

  return (
    <div className={clsx("relative flex flex-col", className)}>
      {label && (
        <label className="label" htmlFor={field}>
          {label}
          {optionalLabel && <span className="ml-1 text-sm font-light">{optionalLabel}</span>}
        </label>
      )}

      <div
        className={clsx("overflow-hidden border-b-input-width", borderStyle, {
          "input-outlined bg-background-primary": outlined && !underlined,
          "input-filled bg-input-background": !outlined && !underlined,
          "hover:border-b-primary": !disabled,
          "resize-y": resizable,
        })}
      >
        <textarea
          {...registration}
          id={field}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          required={required}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            registration.onBlur(event);
            setIsFocused(false);
          }}
          className={clsx(
            "input block h-full w-full resize-none overflow-x-hidden overflow-y-hidden break-words whitespace-pre-wrap border-none bg-transparent",
          )}
          disabled={disabled}
        />
      </div>

      {control.isFetching && <LoadingSkeleton kind="absolute" />}

      {!!(!control.isFetching && (errorMessage || maxLength)) && (
        <div className="mt-2 flex justify-between gap-2">
          <InputError message={errorMessage} className="mt-0" />

          {maxLength && (
            <div className="ml-auto">
              <span className="text-xs font-light">
                {control.watch(field)?.length ?? 0}/{maxLength}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
